// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchParentStudents, fetchStudentProgressionGrid } from '../../services/api';
import {
  canonicalSubject,
  resolveStrandLabel,
  standardProgressScore,
  statusFromColor,
  statusFromScore,
  UI_COLORS,
} from '../../utils/progressStatus';

const SCHOOL_BRAND_BY_NAME = {
  'Stellar Starlight School': {
    logo: '/69e03c5f0ce260dc5b607ea2_Stellar%20Starlight%20Main%20Logo%20-%20Color.png',
  },
  'CALE School': {
    logo: '/cale-logo.png',
  },
};

const panelStyle = {
  borderRadius: 18,
  padding: 18,
  background: 'var(--ae-surface-soft)',
  border: '1px solid var(--ae-border)',
  boxShadow: 'var(--ae-shadow-card)',
};

// Canonical progression sequence for foundational skills
// Earlier items should always be suggested and addressed first.
const LITERACY_PROGRESSION = [
  'print awareness',
  'concepts of print',
  'phonemic awareness',
  'phonological awareness',
  'sounds of language',
  'phonics',
  'decoding',
  'word recognition',
  'word study',
  'fluency',
  'vocabulary',
  'language',
  'comprehension',
  'reading',
  'reading literary text',
  'reading informational text',
  'writing',
  'speaking and listening',
];

const MATH_PROGRESSION = [
  'number sense',
  'counting',
  'cardinality',
  'numbers and operations',
  'number sense and operations in base ten',
  'operations and algebraic thinking',
  'relationships and algebraic thinking',
  'algebraic relationships',
  'fractions',
  'rational numbers',
  'measurement',
  'measurement and data',
  'geometry',
  'geometry and measurement',
  'data and statistics',
  'statistics',
  'probability',
];

function getProgressionRank(subject, strandName, domainName) {
  const normStrand = String(strandName || '').toLowerCase().trim();
  const normDomain = String(domainName || '').toLowerCase().trim();

  const progressionList = subject === 'Literacy'
    ? LITERACY_PROGRESSION
    : (subject === 'Math' ? MATH_PROGRESSION : []);

  // 1. Check strand first, as strand represents the specific skill
  for (let i = 0; i < progressionList.length; i += 1) {
    const key = progressionList[i];
    if (normStrand.includes(key)) {
      return i;
    }
  }

  // 2. Check domain next if strand didn't match a specific step
  for (let i = 0; i < progressionList.length; i += 1) {
    const key = progressionList[i];
    if (normDomain.includes(key)) {
      return i;
    }
  }

  return 999;
}

// Status priority for recommendations:
// Yellow ("working" / Learning Zone) is prioritized FIRST because it reflects the student's
// active Zone of Proximal Development where at-home support has the greatest immediate impact.
function recommendationPriority(status) {
  if (status === 'working') return 1;
  if (status === 'not_yet') return 2;
  return 99;
}

// Builds detailed action data grouped by subject and domain, with individual strand cards
function buildChildActionDetails(rows, gradeLevel) {
  // Map of subject -> Map of domain -> Map of strand -> strand item
  const subjectMap = new Map();

  (rows || []).forEach((entry) => {
    const standard = entry?.standard || {};
    const point = entry?.data_point;
    if (!point) return;

    const score = standardProgressScore(point);
    const color = point?.color;
    // Skip if there is no assessed progress data
    if (score === null && !color) return;

    const grade = String(standard.grade_level || '').toUpperCase();
    const subject = canonicalSubject(standard.subject);
    const status = score !== null ? statusFromScore(score) : statusFromColor(color);
    if (status === 'na') return;

    const domain = String(standard.domain_description || standard.domain || 'General').trim() || 'General';
    const strand = resolveStrandLabel(standard, '') || domain || 'General';
    const standardCode = String(standard.mls_code || '').trim();

    if (!subjectMap.has(subject)) {
      subjectMap.set(subject, new Map());
    }
    const domainMap = subjectMap.get(subject);

    if (!domainMap.has(domain)) {
      domainMap.set(domain, new Map());
    }
    const strandMap = domainMap.get(domain);

    if (!strandMap.has(strand)) {
      strandMap.set(strand, {
        subject,
        domain,
        strand,
        status,
        standardCode,
        grade,
        progressionRank: getProgressionRank(subject, strand, domain),
        allStandards: standardCode ? [standardCode] : [],
        scores: score !== null ? [score] : [],
        hasYellow: status === 'working',
        hasNotYet: status === 'not_yet',
        hasOnTrack: status === 'on_track',
        hasMastered: status === 'mastered',
      });
    } else {
      const existing = strandMap.get(strand);
      if (standardCode && !existing.allStandards.includes(standardCode)) {
        existing.allStandards.push(standardCode);
      }
      if (score !== null) existing.scores.push(score);
      if (status === 'working') existing.hasYellow = true;
      if (status === 'not_yet') existing.hasNotYet = true;
      if (status === 'on_track') existing.hasOnTrack = true;
      if (status === 'mastered') existing.hasMastered = true;

      if (!existing.grade && grade) existing.grade = grade;

      // Calculate aggregate score across assessed standards in this strand
      const avgScore = existing.scores.length
        ? existing.scores.reduce((sum, s) => sum + s, 0) / existing.scores.length
        : null;
      const avgStatus = avgScore !== null ? statusFromScore(avgScore) : null;

      // Prioritize yellow ("working" / Learning Zone):
      // If any assessed standard is yellow OR the strand average is in the yellow zone
      if (existing.hasYellow || avgStatus === 'working') {
        existing.status = 'working';
        if (status === 'working') existing.standardCode = standardCode;
      } else if (existing.hasNotYet || avgStatus === 'not_yet') {
        existing.status = 'not_yet';
        if (status === 'not_yet' && !existing.standardCode) existing.standardCode = standardCode;
      } else if (avgStatus) {
        existing.status = avgStatus;
      }
    }
  });

  // Convert to structured subject list
  const subjects = [];
  const allActionStrands = [];

  const subjectOrder = ['Literacy', 'Math', 'Science', 'Social Studies', 'Success Skills'];
  const allSubjectKeys = Array.from(new Set([...subjectOrder.slice(0, 4), ...subjectMap.keys()]));
  const sortedSubjectKeys = allSubjectKeys.sort((a, b) => {
    const ai = subjectOrder.indexOf(a);
    const bi = subjectOrder.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  sortedSubjectKeys.forEach((subject) => {
    const domainMap = subjectMap.get(subject) || new Map();
    const domains = [];
    let subjectHasYellow = false;
    let subjectHasNeeds = false;

    domainMap.forEach((strandMap, domain) => {
      const strands = Array.from(strandMap.values());
      const yellowStrands = strands.filter((s) => s.hasYellow);
      const notYetStrands = strands.filter((s) => s.status === 'not_yet');
      const needStrands = [...yellowStrands, ...notYetStrands];

      if (yellowStrands.length > 0) subjectHasYellow = true;
      if (needStrands.length > 0) subjectHasNeeds = true;

      // Sort strands by progression order
      needStrands.sort((a, b) => a.progressionRank - b.progressionRank);
      needStrands.forEach((s) => allActionStrands.push(s));

      domains.push({
        domain,
        subject,
        allStrands: strands,
        yellowStrands,
        needStrands,
      });
    });

    subjects.push({
      subject,
      domains,
      hasYellow: subjectHasYellow,
      hasNeeds: subjectHasNeeds,
    });
  });

  // Determine top recommendation for "Suggested Next Step":
  // 1. Pick first yellow following progression order (Literacy print/phonics/fluency first, Math numbers first).
  // 2. If no yellows, pick first not_yet following progression order.
  allActionStrands.sort((a, b) => {
    const prioDiff = recommendationPriority(a.status) - recommendationPriority(b.status);
    if (prioDiff !== 0) return prioDiff;
    const subjOrderA = subjectOrder.indexOf(a.subject);
    const subjOrderB = subjectOrder.indexOf(b.subject);
    if (subjOrderA !== subjOrderB) return (subjOrderA === -1 ? 99 : subjOrderA) - (subjOrderB === -1 ? 99 : subjOrderB);
    return a.progressionRank - b.progressionRank;
  });

  const topPriority = allActionStrands[0] || null;

  return {
    subjects,
    topPriority,
    totalNeeds: allActionStrands.length,
  };
}

const ParentAction = ({
  studentsOverride,
  basePath = '/parent',
  returnTo = { pathname: '/parent/action', label: 'Back to Action' },
  subjectFilter = '',
  showOverview = true,
}: any) => {
  const navigate = useNavigate();
  const usesOverride = Array.isArray(studentsOverride);
  const [fetchedStudents, setStudents] = useState<any>([]);
  const students = usesOverride ? studentsOverride : fetchedStudents;
  const [childActionData, setChildActionData] = useState<any>({});
  const [error, setError] = useState<any>('');
  const [childErrors, setChildErrors] = useState<any>({});

  useEffect(() => {
    if (usesOverride) return undefined;
    let mounted = true;
    fetchParentStudents()
      .then((data) => {
        if (mounted) setStudents(data.students || []);
      })
      .catch(() => {
        if (mounted) setError('Could not load linked student profiles.');
      });
    return () => {
      mounted = false;
    };
  }, [usesOverride]);

  useEffect(() => {
    let cancelled = false;
    students.forEach((student) => {
      if (childActionData[student.student_id]) return;
      fetchStudentProgressionGrid(student.student_id)
        .then((rows) => {
          if (cancelled) return;
          setChildActionData((prev) => ({
            ...prev,
            [student.student_id]: buildChildActionDetails(rows, student.grade_level),
          }));
        })
        .catch(() => {
          if (!cancelled) setChildErrors(previous => ({
            ...previous,
            [student.student_id]: 'Could not load student progress. Please reopen Action to try again.',
          }));
        });
    });
    return () => {
      cancelled = true;
    };
    // Already-loaded students are skipped so switching filters doesn't refetch them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students]);

  const goToSupport = (studentId, item) => {
    const params = new URLSearchParams();
    if (item.subject) params.set('subject', item.subject);
    if (item.domain) params.set('domain', item.domain);
    params.set('studentId', String(studentId));
    navigate(`${basePath}/at-home-support?${params}`, {
      state: { returnTo },
    });
  };

  const childChips = useMemo(() => {
    return students.map((student) => ({
      id: student.student_id,
      name: `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`,
    }));
  }, [students]);

  const enrolledSchools = useMemo(() => {
    const byKey = new Map();

    students.forEach((student) => {
      const classes = Array.isArray(student.classes) ? student.classes : [];

      classes.forEach((cls) => {
        const schoolId = cls?.school_id;
        const schoolName = cls?.school_name || '';
        const key = schoolId != null ? `id:${schoolId}` : `name:${schoolName.toLowerCase()}`;
        if (!schoolName || byKey.has(key)) return;

        const themed = SCHOOL_BRAND_BY_NAME[schoolName] || {};
        byKey.set(key, {
          name: schoolName,
          logo: themed.logo || '/academic-eye-logo.png',
        });
      });

      if (classes.length === 0 && student?.school_name) {
        const schoolName = student.school_name;
        const schoolId = student.school_id;
        const key = schoolId != null ? `id:${schoolId}` : `name:${schoolName.toLowerCase()}`;
        if (byKey.has(key)) return;

        const themed = SCHOOL_BRAND_BY_NAME[schoolName] || {};
        byKey.set(key, {
          name: schoolName,
          logo: themed.logo || '/academic-eye-logo.png',
        });
      }
    });

    return Array.from(byKey.values());
  }, [students]);

  return (
    <div style={{ padding: 6, display: 'grid', gap: 18 }}>
      {showOverview && (
      <section style={{ ...panelStyle, background: '#ffffff', border: '1px solid #dbe4f0' }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#1e3a5f', marginBottom: 10 }}>Academic Eye Action</div>
        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>
          Use Guided Actions to focus on the next supports your family can provide. Student Goals: Coming soon.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {childChips.map((child) => (
            <span key={child.id} style={{ background: '#FFF0E3', border: '1px solid #FFD2B0', borderRadius: 999, padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#1e3a5f' }}>
              {child.name}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {enrolledSchools.length === 0 ? (
            <div style={{ background: '#f8fafc', border: '1px dashed #bed1ef', borderRadius: 12, padding: '8px 14px', fontSize: 12, color: '#64748b' }}>
              No enrolled school found yet for linked students.
            </div>
          ) : enrolledSchools.map((brand) => (
            <div key={brand.name} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', border: '1px solid #dbe4f0', borderRadius: 999, padding: '8px 14px' }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: '#40669D', border: '1px solid #dbe4f0', overflow: 'hidden', flex: '0 0 auto' }}>
                <img src={brand.logo} alt={brand.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit', display: 'block' }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#1e3a5f' }}>{brand.name}</span>
            </div>
          ))}
        </div>
      </section>
      )}

      {students.map((student) => {
        const childName = `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`;
        const actionDetails = childActionData[student.student_id];
        const isLoading = !actionDetails;
        const childError = childErrors[student.student_id];
        const subjects = (actionDetails?.subjects || []).filter((subject) => !subjectFilter || subject.subject === subjectFilter);
        const nextStepDomains = subjects.flatMap(subject =>
          subject.domains.filter(domain => domain.yellowStrands.length > 0));

        return (
          <div key={student.student_id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.85fr) minmax(0, 1.15fr)', gap: 18 }}>
            <section style={panelStyle} aria-label={`${childName} suggested next steps`}>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: '#1e3a5f' }}>{childName} · Suggested Next Steps</div>
              <div style={{ minHeight: 160, borderRadius: 16, background: 'white', border: '1px solid #d1d5db', padding: 18, display: 'grid', gap: 12, alignContent: 'start' }}>
                {childError ? <div role="alert">{childError}</div> : isLoading ? (
                  <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading progress...</div>
                ) : nextStepDomains.length > 0 ? nextStepDomains.map(domain => (
                  <button
                    key={`${domain.subject}:${domain.domain}`}
                    type="button"
                    onClick={() => goToSupport(student.student_id, domain)}
                    style={{ textAlign: 'left', cursor: 'pointer', borderRadius: 14, background: '#FEF9C3', border: '1px solid #FDE047', padding: '14px 16px', color: '#854D0E' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{domain.subject}</span>
                      <span style={{ fontSize: 11, fontWeight: 800, background: '#FDE047', color: '#713F12', padding: '2px 8px', borderRadius: 999 }}>Learning Zone</span>
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.5, fontWeight: 700, color: '#713F12' }}>{domain.domain}</div>
                    <div style={{ fontSize: 11, marginTop: 8, fontWeight: 600, color: '#854D0E', opacity: 0.9 }}>Tap for guided activities and support ideas →</div>
                  </button>
                )) : (
                  <div style={{ borderRadius: 14, background: '#ECFDF5', border: '1px solid #A7F3D0', padding: '16px', color: '#065f46', fontSize: 13, lineHeight: 1.6, fontWeight: 700 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 16 }}>🎉</span>
                      <span style={{ fontSize: 14, fontWeight: 800 }}>All on track!</span>
                    </div>
                    <div>Great news — no current action recommendations right now. Keep up the consistent support at home.</div>
                  </div>
                )}
              </div>
            </section>

            <section style={panelStyle} aria-label={`${childName} action cards`}>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 14, color: '#1e3a5f' }}>{childName} · Guided Actions</div>
              <div style={{ display: 'grid', gap: 14 }}>
                {childError ? <div role="alert">{childError}</div> : isLoading ? (
                  <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading progress...</div>
                ) : subjects.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: 13 }}>No subject data available.</div>
                ) : subjects.map((subj) => {
                  const yellowDomains = subj.domains.filter((d) => d.yellowStrands.length > 0);
                  const hasYellows = yellowDomains.length > 0;

                  return (
                    <div
                      key={subj.subject}
                      style={{
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: 14,
                        padding: '14px 16px',
                        display: 'grid',
                        gap: 12,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: '#1e3a5f' }}>{subj.subject}</span>
                        {!hasYellows && (
                          <span style={{ fontSize: 11, fontWeight: 700, background: '#ECFDF5', color: '#059669', border: '1px solid #A7F3D0', padding: '3px 10px', borderRadius: 999 }}>
                            No current needs · All on track
                          </span>
                        )}
                      </div>

                      {hasYellows ? (
                        <div style={{ display: 'grid', gap: 10 }}>
                          {yellowDomains.map((dom) => (
                            <div
                              key={dom.domain}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(120px, 160px) minmax(0, 1fr)',
                                gap: 12,
                                alignItems: 'center',
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 10,
                                padding: '10px 12px',
                              }}
                            >
                              {/* Domain on the left */}
                              <div style={{ borderRight: '1px solid #e2e8f0', paddingRight: 8 }}>
                                <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b' }}>Domain</div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#1e3a5f', lineHeight: 1.3 }}>{dom.domain}</div>
                              </div>

                              {/* Yellow strands on the right */}
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {dom.yellowStrands.map((strandItem) => (
                                  <button
                                    key={strandItem.strand}
                                    type="button"
                                    onClick={() => goToSupport(student.student_id, strandItem)}
                                    style={{
                                      borderRadius: 8,
                                      padding: '8px 12px',
                                      background: UI_COLORS.working,
                                      color: '#713F12',
                                      fontWeight: 700,
                                      fontSize: 12,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 6,
                                      border: '1px solid #FDE047',
                                      cursor: 'pointer',
                                      textAlign: 'left',
                                    }}
                                  >
                                    <span>{strandItem.strand}</span>
                                    <span style={{ fontSize: 11, opacity: 0.8 }}>→</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic', padding: '4px 0' }}>
                          No learning zone strands requiring action in {subj.subject}.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        );
      })}

      {error ? <div role="alert">{error}</div> : !usesOverride && students.length === 0 && (
        <div style={{ ...panelStyle, background: '#ffffff', color: '#64748b', fontSize: 13 }}>
          No linked student profiles yet.
        </div>
      )}
    </div>
  );
};

// Exported for testing
export {
  LITERACY_PROGRESSION,
  MATH_PROGRESSION,
  getProgressionRank,
  recommendationPriority,
  buildChildActionDetails,
};

export default ParentAction;
